import { useEffect, useState, useRef } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { Heart, CheckCircle2, Loader2, XCircle, ArrowRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_ATTEMPTS = 8;
const INTERVAL = 2000;

export default function DonateSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking");
  const [data, setData] = useState(null);
  const attemptsRef = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setStatus("error");
      return;
    }
    let cancelled = false;
    const poll = async () => {
      if (cancelled) return;
      if (attemptsRef.current >= MAX_ATTEMPTS) {
        setStatus("pending");
        return;
      }
      attemptsRef.current += 1;
      try {
        const { data: resp } = await axios.get(`${API}/donations/status/${sessionId}`);
        setData(resp);
        if (resp.payment_status === "paid") { setStatus("paid"); return; }
        if (resp.status === "expired") { setStatus("expired"); return; }
        setTimeout(poll, INTERVAL);
      } catch (err) {
        setStatus("error");
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <div className="min-h-[80vh] flex items-center justify-center py-20 px-6 bg-white" data-testid="donate-success-page">
      <div className="max-w-xl w-full gh-card !p-10 lg:!p-14 text-center">
        {status === "checking" && (
          <>
            <Loader2 className="w-14 h-14 text-teal mx-auto mb-6 animate-spin" />
            <h1 className="font-display text-3xl sm:text-4xl text-navy mb-3">Confirming your gift...</h1>
            <p className="text-muted-ink">One moment — we're verifying with Stripe.</p>
          </>
        )}
        {status === "paid" && (
          <>
            <div className="w-20 h-20 rounded-full bg-teal-light flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="w-10 h-10 text-teal" />
            </div>
            <p className="eyebrow mb-4">Thank you</p>
            <h1 className="font-display text-4xl sm:text-5xl text-navy mb-5 leading-[1.1]">
              Your ${data?.amount} gift just <span className="hl-teal">restored access</span> for someone.
            </h1>
            <p className="text-lg text-muted-ink leading-relaxed mb-8">
              Because of you, someone reaching out tonight won't be turned away by cost or coverage.
              That's the kind of impact that ripples forward, across generations.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/" className="btn-primary" data-testid="success-home-link">
                Back to home <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/resources" className="btn-outline" data-testid="success-resources-link">
                Explore resources
              </Link>
            </div>
          </>
        )}
        {status === "pending" && (
          <>
            <Heart className="w-14 h-14 text-teal mx-auto mb-6" />
            <h1 className="font-display text-3xl sm:text-4xl text-navy mb-4">Payment is processing</h1>
            <p className="text-muted-ink mb-8">
              This can sometimes take a moment. We'll send a confirmation email once it's complete.
            </p>
            <Link to="/" className="btn-outline">Return home</Link>
          </>
        )}
        {status === "expired" && (
          <>
            <XCircle className="w-14 h-14 text-red-600 mx-auto mb-6" />
            <h1 className="font-display text-3xl sm:text-4xl text-navy mb-4">Session expired</h1>
            <p className="text-muted-ink mb-8">The checkout session expired before completing. Please try again.</p>
            <Link to="/donate" className="btn-primary">Try again</Link>
          </>
        )}
        {status === "error" && (
          <>
            <XCircle className="w-14 h-14 text-red-600 mx-auto mb-6" />
            <h1 className="font-display text-3xl sm:text-4xl text-navy mb-4">Something went wrong</h1>
            <p className="text-muted-ink mb-8">We couldn't verify your payment. If you were charged, please contact us and we'll sort it out.</p>
            <Link to="/contact" className="btn-primary">Contact us</Link>
          </>
        )}
      </div>
    </div>
  );
}
